// @mui material components
import React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Senthil Solar React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Senthil Solar React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Icons
import PowerOffIcon from "@mui/icons-material/PowerOff";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import PublicIcon from "@mui/icons-material/Public";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images - Use an existing image or add 'offgrid_bg.jpg' to src/assets/images/
// For now, reusing 'canva_banner.png' from Awards as a placeholder; replace with an off-grid themed bg
import bgImage from "assets/images/canva_banner.png";

// Animation imports
import { useSpring, animated, useTrail } from 'react-spring';
import { useInView } from 'react-intersection-observer';

// Create animated versions of MUI components
const AnimatedGrid = animated(Grid);
const AnimatedMKBox = animated(MKBox);

function OffGridService() {
  // For header animation
  const [headerRef, headerInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });
  
  const headerAnimation = useSpring({
    opacity: headerInView ? 1 : 0,
    transform: headerInView ? 'translateY(0)' : 'translateY(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  // For system components section
  const [componentsRef, componentsInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  const componentsHeaderAnimation = useSpring({
    opacity: componentsInView ? 1 : 0,
    transform: componentsInView ? 'translateY(0)' : 'translateY(30px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const systemComponents = [
    {
      component: "Solar Panels",
      description: "High-efficiency polycrystalline or monocrystalline panels, 25-year performance warranty"
    },
    {
      component: "Battery Bank",
      description: "Lithium-ion or tubular lead-acid batteries, 5-10 years life, multiple days backup"
    },
    {
      component: "Off-Grid Inverter",
      description: "Pure sine wave inverter with MPPT charge controller, 95%+ efficiency"
    },
    {
      component: "Monitoring System",
      description: "Real-time monitoring of generation, consumption, and battery status via mobile app"
    }
  ];

  const [componentsItemsRef, componentsItemsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const componentsTrail = useTrail(systemComponents.length, {
    opacity: componentsItemsInView ? 1 : 0,
    transform: componentsItemsInView ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For applications section
  const [applicationsRef, applicationsInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const applicationsAnimation = useSpring({
    opacity: applicationsInView ? 1 : 0,
    transform: applicationsInView ? 'translateY(0)' : 'translateY(50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const applications = [
    {
      icon: <HomeWorkIcon fontSize="large" />,
      title: "Remote Homes",
      description: "Perfect for farmhouses, hill stations, and locations without grid connectivity"
    },
    {
      icon: <PublicIcon fontSize="large" />,
      title: "Telecom Towers",
      description: "Reliable power for mobile towers in remote areas, reducing diesel dependency"
    },
    {
      icon: <FlashOnIcon fontSize="large" />,
      title: "Emergency Backup",
      description: "Complete power backup for critical facilities like hospitals and data centers"
    }
  ];

  const [appsItemsRef, appsItemsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const appsTrail = useTrail(applications.length, {
    opacity: appsItemsInView ? 1 : 0,
    transform: appsItemsInView ? 'scale(1)' : 'scale(0.8)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For key advantages section
  const [advantagesRef, advantagesInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const advantagesHeaderAnimation = useSpring({
    opacity: advantagesInView ? 1 : 0,
    transform: advantagesInView ? 'translateX(0)' : 'translateX(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const [advantagesItemsRef, advantagesItemsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const advantagesTrail = useTrail(3, {
    opacity: advantagesItemsInView ? 1 : 0,
    transform: advantagesItemsInView ? 'translateY(0)' : 'translateY(30px)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For custom design section
  const [designRef, designInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const designAnimation = useSpring({
    opacity: designInView ? 1 : 0,
    transform: designInView ? 'translateY(0)' : 'translateY(50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });


  return (
    <>
      <DefaultNavbar
        routes={routes}
        transparent
        light
      />
      <MKBox
        minHeight="60vh"
        width="100%"
        sx={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <AnimatedGrid
            ref={headerRef}
            style={headerAnimation}
            container
            item
            xs={12}
            lg={7}
            justifyContent="center"
            mx="auto"
          >
            <MKTypography
              variant="h1"
              color="white"
              mt={-6}
              mb={1}
              sx={({ breakpoints, typography: { size } }) => ({
                textAlign: "center",
                // Responsive font sizes
                [breakpoints.down("sm")]: {
                  fontSize: size["xl"],   // very small screens
                },
                [breakpoints.between("sm", "md")]: {
                  fontSize: size["2xl"],  // tablets
                },
                [breakpoints.up("md")]: {
                  fontSize: size["3xl"],  // desktops
                },
              })}
            >
              Off-Grid Solar
              <br />
              Systems
            </MKTypography>

            <MKTypography
              variant="h5"
              color="white"
              textAlign="center"
              sx={({ breakpoints, typography: { size } }) => ({
                opacity: 0.9,
                [breakpoints.down("sm")]: {
                  fontSize: size["md"],   // smaller subtitle on phones
                },
                [breakpoints.between("sm", "md")]: {
                  fontSize: size["lg"],   // tablets
                },
                [breakpoints.up("md")]: {
                  fontSize: size["xl"],   // desktops
                },
              })}
            >
              Complete Energy Independence for Remote & Critical Applications
            </MKTypography>
          </AnimatedGrid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <Grid container item xs={12} lg={10} mx="auto" mb={4}>
              <MKTypography variant="body1" color="text" textAlign="center">
                Off-grid solar systems provide complete energy independence by generating and storing 
                electricity for use anytime, day or night. Unlike grid-tied systems, off-grid setups 
                include battery storage to ensure uninterrupted power supply even when the sun is not 
                shining. These systems are ideal for locations without grid access or for those seeking 
                complete self-sufficiency.
              </MKTypography>
              <MKTypography variant="body1" color="text" textAlign="center" mt={2}>
                Senthil Solar designs custom off-grid solutions ranging from small 1kW home systems to 
                large 100kW+ installations for commercial and industrial applications. Our systems are 
                engineered to provide reliable power 365 days a year, even during extended monsoon periods.
              </MKTypography>
            </Grid>
            
            {/* System Components Section */}
            <AnimatedGrid 
              ref={componentsRef}
              style={componentsHeaderAnimation}
              container 
              item 
              xs={12} 
              justifyContent="center" 
              mx="auto" 
              mb={4}
            >
              <MKTypography variant="h4" textAlign="center" mb={4}>
                System Components
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={3} ref={componentsItemsRef}>
              {componentsTrail.map((style, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <AnimatedMKBox 
                    style={style}
                    p={3} 
                    borderRadius="lg"
                    sx={{ 
                      backgroundColor: "rgba(244, 67, 54, 0.05)",
                      border: "1px solid rgba(244, 67, 54, 0.2)",
                      height: "100%"
                    }}
                  >
                    <MKTypography variant="h6" color="error.main" mb={1}>
                      {systemComponents[index].component}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {systemComponents[index].description}
                    </MKTypography>
                  </AnimatedMKBox>
                </Grid>
              ))}
            </Grid>

            {/* Perfect Applications Section */}
            <AnimatedGrid 
              ref={applicationsRef}
              style={applicationsAnimation}
              container 
              item 
              xs={12} 
              md={10} 
              lg={8} 
              justifyContent="center" 
              mx="auto" 
              mt={6}
            >
              <MKTypography variant="h4" textAlign="center" mb={4}>
                Perfect Applications
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={4} ref={appsItemsRef}>
              {appsTrail.map((style, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <animated.div style={style}>
                    <MKBox textAlign="center">
                      <MKBox color="error.main" mb={2}>
                        {React.cloneElement(applications[index].icon, { fontSize: "large" })}
                      </MKBox>
                      <MKTypography variant="h5" mb={1}>
                        {applications[index].title}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {applications[index].description}
                      </MKTypography>
                    </MKBox>
                  </animated.div>
                </Grid>
              ))}
            </Grid>

            {/* Key Advantages Section */}
            <AnimatedGrid 
              ref={advantagesRef}
              style={advantagesHeaderAnimation}
              container 
              item 
              xs={12} 
              md={10} 
              lg={8} 
              justifyContent="center" 
              mx="auto" 
              mt={6}
            >
              <MKTypography variant="h4" mb={3} textAlign="center">
                Key Advantages
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={2} ref={advantagesItemsRef}>
              {advantagesTrail.map((style, index) => {
                const advantages = [
                  { icon: <BatteryChargingFullIcon />, title: "24/7 Power Availability", desc: "Battery storage ensures power is available round the clock, regardless of weather conditions or time of day." },
                  { icon: <PowerOffIcon />, title: "No Grid Dependency", desc: "Perfect for remote locations, eliminates electricity bills, immune to grid outages and power quality issues." },
                  { icon: <FlashOnIcon />, title: "Scalable & Modular", desc: "Systems can be easily expanded by adding more panels or batteries as energy needs grow over time." }
                ];
                return (
                  <Grid item xs={12} key={index}>
                    <animated.div style={style}>
                      <MKBox display="flex" alignItems="center" justifyContent="center" mb={3} p={2} borderRadius="lg" sx={{ backgroundColor: "rgba(244, 67, 54, 0.05)" }}>
                        <MKBox color={index === 0 ? "success.main" : index === 1 ? "info.main" : "warning.main"} mr={2}>
                          {React.cloneElement(advantages[index].icon, { fontSize: "large" })}
                        </MKBox>
                        <MKBox textAlign="center" flex={1}>
                          <MKTypography variant="h6" mb={0.5}>
                            {advantages[index].title}
                          </MKTypography>
                          <MKTypography variant="body2" color="text">
                            {advantages[index].desc}
                          </MKTypography>
                        </MKBox>
                      </MKBox>
                    </animated.div>
                  </Grid>
                );
              })}
            </Grid>

            {/* Custom Design Process Section */}
            <AnimatedMKBox 
              ref={designRef}
              style={designAnimation}
              component="section" 
              p={4} 
              borderRadius="lg" 
              mb={5}
              mt={6}
              sx={{ 
                background: "linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%)"
              }}
            >
              <MKTypography variant="h5" mb={2}>
                Custom Design Process
              </MKTypography>
              <MKTypography variant="body1" color="text" mb={2}>
                Every off-grid system we design is customized based on your specific load requirements, 
                backup needs, available roof space, and budget. Our engineering team conducts detailed 
                load analysis and site assessment to determine optimal system sizing.
              </MKTypography>
              <MKTypography variant="body1" color="text">
                We factor in seasonal variations, worst-case scenarios (continuous cloudy days), and 
                future load expansion to ensure your system performs reliably throughout its 25+ year 
                lifespan. All systems include comprehensive warranties and AMC options.
              </MKTypography>
            </AnimatedMKBox>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default OffGridService;